import { DOMAIN } from 'config';
import { msg } from '@lingui/macro';
import { i18n, type MessageDescriptor } from '@lingui/core';
import { checkFeatureFlag } from 'modules/accessControl/utils';
import type { FeatureFlags } from 'modules/accessControl/types';

/**
 * Legacy iframe detail views that show a D2 breadcrumb back to the React list/table.
 * Add a new entry here when another area needs the same back navigation.
 */
/** Default D2 table URL search — keep in sync with Styles.tsx / Items.tsx useUrlState defaults. */
export const IFRAME_D2_TABLE_DEFAULT_SEARCH = '?sort=name&order=ASC';

export type IframeDetailBreadcrumbRule = {
  id: string;
  featureFlag: string;
  /** Must match detail routes, e.g. /^\/style\/(\d+)/ */
  detailPathPattern: RegExp;
  /** Pathname only (e.g. /style). */
  listPath: string;
  listSearch: string;
  listLabel: MessageDescriptor;
  detailNameFallback?: MessageDescriptor;
};

export const iframeDetailBreadcrumbRules: IframeDetailBreadcrumbRule[] = [
  {
    id: 'style',
    featureFlag: 'StylesTableView',
    detailPathPattern: /^\/style\/(\d+)/,
    listPath: '/style',
    listSearch: IFRAME_D2_TABLE_DEFAULT_SEARCH,
    listLabel: msg`Styles`,
  },
  {
    id: 'style-create',
    featureFlag: 'StylesTableView',
    detailPathPattern: /^\/style\/create/,
    listPath: '/style',
    listSearch: IFRAME_D2_TABLE_DEFAULT_SEARCH,
    listLabel: msg`Styles`,
    detailNameFallback: msg`Create style`,
  },
  {
    id: 'item',
    featureFlag: 'ItemsTableView',
    detailPathPattern: /^\/item\/(\d+)/,
    listPath: '/item',
    listSearch: IFRAME_D2_TABLE_DEFAULT_SEARCH,
    listLabel: msg`Items`,
  },
  {
    id: 'item-create',
    featureFlag: 'ItemsTableView',
    detailPathPattern: /^\/item\/create/,
    listPath: '/item',
    listSearch: IFRAME_D2_TABLE_DEFAULT_SEARCH,
    listLabel: msg`Items`,
    detailNameFallback: msg`Create item`,
  },
];

export function getIframeListUrl(rule: IframeDetailBreadcrumbRule): string {
  return `${rule.listPath}${rule.listSearch}`;
}

export type IframeDetailBreadcrumb = {
  id: string;
  entity: string;
  listLabel: string;
  backTo: string;
  name?: string;
};

export function getIframeDetailBreadcrumb(
  pathname: string,
  featureFlags?: FeatureFlags,
  translate: (descriptor: MessageDescriptor) => string = descriptor => i18n._(descriptor)
): IframeDetailBreadcrumb | null {
  const rule = iframeDetailBreadcrumbRules.find(
    r => checkFeatureFlag(r.featureFlag, featureFlags) && r.detailPathPattern.test(pathname)
  );
  if (!rule) {
    return null;
  }
  return {
    id: rule.id,
    entity: rule.id,
    listLabel: translate(rule.listLabel),
    backTo: getIframeListUrl(rule),
    ...(rule.detailNameFallback && { name: translate(rule.detailNameFallback) }),
  };
}

export const pathConverter = (path: string = '', reloadKey = 0, auth0OrgId?: string | null) => {
  const reload = reloadKey > 0 ? `&_r=${reloadKey}` : '';
  const orgParam = auth0OrgId ? `&_auth0org=${encodeURIComponent(auth0OrgId)}` : '';
  return `${DOMAIN}/?mode=headerless${reload}${orgParam}#${path}`;
};

export const iframePaths = [
  '/item', // Controlled by ItemsTableView feature flag
  '/style',
  '/report',
  '/dashboard',
  //'/analytics', // disabled due to flex monster not working in iframe
  '/marketing',
  '/order',
  '/compliance',
  '/admin',
];

// Regex patterns for paths that should show iframe (detail/sub-routes)
export const iframePathPatterns = iframeDetailBreadcrumbRules.map(rule => rule.detailPathPattern);

export const shouldNavigate = (iframePath: string, reactPath: string): string => {
  try {
    const iframeUrl = (iframePath || '').replace(/^#\/|^#/, '').replace(/^\/+/, '');
    const reactUrl = reactPath.replace(/^\/+/, '');
    if (!iframeUrl || iframeUrl === reactUrl) {
      return '';
    }
    return `/${iframeUrl}`;
  } catch (e) {
    return '';
  }
};

export const checkIframeOrigin = (origin: string): boolean => {
  if (origin === DOMAIN) {
    return true;
  }
  if (origin === window.location.origin) {
    return true;
  }
  return false;
};

export const matchesIframeActivePath = (path: string, activePaths: string[]) =>
  activePaths.includes(path) ||
  activePaths.some(p => path.toLowerCase().includes(p)) ||
  iframePathPatterns.some(pattern => pattern.test(path));

export const isIframeStyleOrItemDetailPath = (pathname: string): boolean =>
  iframePathPatterns.some(pattern => pattern.test(pathname));
